"use client"
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAssistantContext } from "../../contexts/AssistantContext";
import { useAuth } from "../../useAuth";
import { DEFAULT_EXECUTION_MODE, EXECUTION_MODE_OPTIONS } from "../../types/assistant.types";
// eslint-disable-next-line no-unused-vars
import { useOrganization } from "../../contexts/OrganizationContext";
import StyledSelect from "../ui/StyledSelect";
import useToast from "../../hooks/useToast";
import {
  Plus,
  LayoutGrid,
  List as LayoutList,
  MoreHorizontal,
  X,
  Loader,
  MessageSquare,
  Headset,
  Search,
  XCircle,
  Clock,
  Cpu,
  Wrench,
  Trash2
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import ToastContainer from "../ui/ToastContainer";
import ConfirmDialog from "../ui/ConfirmDialog";
import { MetaChip, StatTile, formatDate, updatedAtOf } from "../dashboard/ResourceBits";
import assistantsBot from "../../assets/assistants-bot.png";
import { useZeno } from "../../hooks/useZeno";
import AssistantService from "../../services/assistant.service";
import ChatService from "../../services/chat.service";
import ZenoService from "../../services/zeno.service";
import IntegrationService from "../../services/integration.service";
import ZenitThinking from "../ui/ZenitThinking";
import AssistantConfigureView from "./AssistantConfigureView";
import AssistantCreateSteps from "./AssistantCreateSteps";
import { pruneSkillSettings, pruneSkillIntegrations } from "./SkillStoreSelector";
import AvatarsView from "../AvatarsView/AvatarsView";
import { estimateBudgetForAttachments } from "../../utils/tokenEstimation";

// Zeno Bot ID constant
const ZENO_ASSISTANT_ID = '00000000-0000-0000-0000-0000000000e0';
const DEFAULT_TEMPLATE_ID = "00000000-0000-0000-0000-000000000001";
const NO_AVATAR_TEMPLATE_ID = "00000000-0000-0000-0000-000000000000";
const ASSISTANT_CREATE_DRAFT_KEY = "zenit.assistant.create.draft.v1";
const ASSISTANT_CREATE_DRAFT_MAX_AGE_MS = 1000 * 60 * 60 * 6;
const MAX_CHAT_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const INTEGRATION_REQUIREMENT_META = {
  gmail: { label: "Google Gmail" },
  drive: { label: "Google Drive" },
  calendar: { label: "Google Calendar" },
  google: { label: "Google" },
  whatsapp: { label: "WhatsApp Business" },
  shopify: { label: "Shopify" },
};

function detectSkillIntegrationRequirement(skill) {
  const explicit = String(
    skill?.provider ||
      skill?.service ||
      skill?.service_type ||
      skill?.integration ||
      skill?.integration_type ||
      skill?.category ||
      ""
  ).toLowerCase();

  const haystack = `${explicit} ${skill?.name || ""} ${skill?.friendly_name || ""} ${skill?.description || ""}`.toLowerCase();

  if (/(whatsapp|meta)/.test(haystack)) return "whatsapp";
  if (/shopify/.test(haystack)) return "shopify";
  if (/(^|[_\s-])gmail|google\s*mail/.test(haystack)) return "gmail";
  if (/(^|[_\s-])drive|google\s*drive/.test(haystack)) return "drive";
  if (/(^|[_\s-])calendar|google\s*calendar/.test(haystack)) return "calendar";
  if (/google/.test(haystack)) return "google";

  return null;
}

function isIntegrationRequirementConnected(requirement, integrationSnapshot) {
  const safeSnapshot = integrationSnapshot || {};
  const googleIntegrations = Array.isArray(safeSnapshot.google) ? safeSnapshot.google.filter((item) => item?.is_active !== false) : [];
  const metaIntegrations = Array.isArray(safeSnapshot.meta) ? safeSnapshot.meta.filter((item) => item?.is_active !== false) : [];
  const shopifyIntegrations = Array.isArray(safeSnapshot.shopify) ? safeSnapshot.shopify.filter((item) => item?.is_active !== false) : [];

  const googleServices = new Set(
    googleIntegrations
      .map((item) => String(item?.service_type || item?.service || "").toLowerCase())
      .filter(Boolean)
  );

  if (requirement === "gmail") return googleServices.has("gmail");
  if (requirement === "drive") return googleServices.has("drive");
  if (requirement === "calendar") return googleServices.has("calendar");
  if (requirement === "google") return googleIntegrations.length > 0;
  if (requirement === "whatsapp") return metaIntegrations.length > 0;
  if (requirement === "shopify") return shopifyIntegrations.length > 0;

  return true;
}

function getMissingSkillIntegrations(selectedSkills, availableSkills, integrationSnapshot) {
  const safeSelected = Array.isArray(selectedSkills) ? selectedSkills : [];
  const safeAvailable = Array.isArray(availableSkills) ? availableSkills : [];
  const missingByRequirement = new Map();

  safeSelected.forEach((skillName) => {
    const skill = safeAvailable.find(
      (item) => item?.name === skillName || item?.id === skillName || item?.friendly_name === skillName
    );

    const requirement = detectSkillIntegrationRequirement(skill || { name: skillName, friendly_name: skillName });
    if (!requirement) return;

    if (isIntegrationRequirementConnected(requirement, integrationSnapshot)) return;

    if (!missingByRequirement.has(requirement)) {
      missingByRequirement.set(requirement, {
        key: requirement,
        label: INTEGRATION_REQUIREMENT_META[requirement]?.label || requirement,
        skills: [],
      });
    }

    const entry = missingByRequirement.get(requirement);
    entry.skills.push(skill?.friendly_name || skillName);
  });

  return Array.from(missingByRequirement.values());
}

const MARKDOWN_FENCE_REGEX = /```(?:markdown|md)\s*\n?([\s\S]*?)```/gi;

function normalizeAssistantMarkdown(content) {
  if (content === null || content === undefined) {
    return "";
  }

  const text = typeof content === "string" ? content : String(content);
  const normalizedLineBreaks = text.replace(/\r\n?/g, "\n");

  // Backend often sends markdown wrapped in ```markdown fences; unwrap it for rich rendering.
  return normalizedLineBreaks.replace(MARKDOWN_FENCE_REGEX, (_match, inner) => (inner || "").trimEnd());
}

const assistantMarkdownComponents = {
  h1: ({ children, ...props }) => (
    <h1 className="mb-3 mt-2 text-lg font-semibold text-white" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="mb-2 mt-2 text-base font-semibold text-white" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="mb-2 mt-2 text-sm font-semibold text-gray-100" {...props}>{children}</h3>
  ),
  p: ({ children, ...props }) => (
    <p className="mb-3 whitespace-pre-wrap leading-relaxed text-gray-100" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="mb-3 ml-2 list-disc space-y-2 pl-5 text-gray-100" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="mb-3 ml-2 list-decimal space-y-2 pl-5 text-gray-100" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-7" {...props}>{children}</li>
  ),
  a: ({ children, ...props }) => (
    <a className="break-all text-purple-200 underline underline-offset-2 hover:text-purple-100" {...props}>{children}</a>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote className="mb-3 border-l-2 border-purple-300/40 pl-3 italic text-gray-300" {...props}>{children}</blockquote>
  ),
  code({ inline, className, children, ...props }) {
    if (inline) {
      return (
        <code className="rounded bg-black/35 px-1.5 py-0.5 text-xs text-purple-100" {...props}>
          {children}
        </code>
      );
    }

    return (
      <pre className="mb-3 overflow-x-auto rounded-xl border border-white/10 bg-black/40 p-3 text-xs leading-relaxed text-gray-100">
        <code className={className} {...props}>{children}</code>
      </pre>
    );
  },
  img: ({ alt, ...props }) => (
    <img
      {...props}
      alt={alt || ""}
      className="my-2 h-auto max-w-full rounded-lg"
      loading="lazy"
      crossOrigin="anonymous"
      referrerPolicy="no-referrer"
    />
  ),
};

// ---------------------------------------------------------------------------
// Agent card helpers
//
// The `summary` view of an assistant returns: name, description, avatar,
// skills[], config.modelLabel, executionMode, isDraft, projectId, updatedAt.
// ---------------------------------------------------------------------------

const modelOf = (a) => a?.config?.modelLabel || a?.config?.model_label || null;
const skillsOf = (a) => (Array.isArray(a?.skills) ? a.skills : []);

/** Shared actions menu — was duplicated across the grid card and the list row. */
const AgentCardMenu = ({ assistant, onChatClick, onDelete, className = "" }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className={`flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-white/10 hover:text-white ${className}`}
        onClick={(e) => e.stopPropagation()}
        aria-label="Agent actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="rounded-xl border border-gray-800 bg-black/90 shadow-lg">
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onChatClick(assistant);
        }}
        className="cursor-pointer text-slate-300 hover:bg-black/60 hover:text-white"
      >
        <MessageSquare className="mr-2 h-4 w-4" />
        Chat
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onDelete(assistant.id);
        }}
        className="cursor-pointer text-red-400 hover:bg-red-900/60 hover:text-red-300"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const AssistantCard = React.memo(React.forwardRef(({
  assistant,
  viewMode = "grid",
  onClick,
  onChatClick,
  onDelete,
  assistantsBot
}, ref) => {
  const model = modelOf(assistant);
  const skills = skillsOf(assistant);
  const mode = assistant.executionMode || assistant.execution_mode;

  if (viewMode === "grid") {
    // En móvil la rejilla se lee como una biblioteca de portadas: tres fichas
    // por fila, la imagen cuadrada mandando y debajo sólo el nombre y una
    // línea de contexto. Un agente se reconoce por su cara antes que por su
    // descripción, y esa descripción a 110px de ancho no cabía de todos modos.
    // De `md` para arriba vuelve la tarjeta completa, que ahí sí tiene sitio.
    const subtitle = [mode, skills.length ? `${skills.length} ${skills.length === 1 ? "skill" : "skills"}` : null]
      .filter(Boolean)
      .join(" · ");

    return (
      <>
        <div
          ref={ref}
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); }
          }}
          className="group cursor-pointer md:hidden focus-visible:outline-none"
        >
          <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black/40 ring-1 ring-inset ring-white/10 group-focus-visible:ring-2 group-focus-visible:ring-violet-400">
            <img
              src={assistant.avatar || assistantsBot}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {/* El ⋯ se queda sólo aquí, y sobre la portada para no romper la
                columna de texto: borrar un agente no existe en ningún otro
                sitio de la app, así que quitarlo de la ficha sería quitar la
                acción. En avatares sí desaparece — allí Borrar y Duplicar
                viven dentro del avatar. */}
            <AgentCardMenu
              assistant={assistant}
              onChatClick={onChatClick}
              onDelete={onDelete}
              className="absolute right-1 top-1 rounded-full bg-black/60 backdrop-blur-sm"
            />
          </div>
          <span className="mt-2 line-clamp-2 block text-[13px] font-medium leading-snug text-white">
            {assistant.name}
          </span>
          {subtitle && (
            <span className="mt-0.5 block truncate text-[12px] text-gray-400">{subtitle}</span>
          )}
        </div>

        <div
          onClick={onClick}
          className="group relative hidden cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/10 md:flex"
        >
          {/* Cover image */}
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-black/40">
            <img
              src={assistant.avatar || assistantsBot}
              alt={assistant.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
              decoding="async"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0e0e13] to-transparent" />
          </div>

          {/* Body */}
          <div className="flex min-w-0 flex-1 flex-col p-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="truncate text-base font-semibold text-white">{assistant.name}</h3>
              <AgentCardMenu
                assistant={assistant}
                onChatClick={onChatClick}
                onDelete={onDelete}
                className="-mt-1 flex-shrink-0 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
              />
            </div>

            <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-400">
              {assistant.description || "No description provided"}
            </p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <MetaChip>{model}</MetaChip>
              <MetaChip>{mode}</MetaChip>
              {skills.length > 0 && (
                <MetaChip>{skills.length} {skills.length === 1 ? "skill" : "skills"}</MetaChip>
              )}
            </div>

            <div className="mt-auto flex items-center gap-2 border-t border-white/10 pt-3">
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Updated {formatDate(updatedAtOf(assistant))}</span>
              </span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // List row
  return (
    <div
      ref={ref}
      onClick={onClick}
      className="group relative flex cursor-pointer items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 transition-all duration-300 hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/10"
    >
      <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black/30">
        <img
          src={assistant.avatar || assistantsBot}
          alt={assistant.name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-medium text-white">{assistant.name}</h3>
        </div>
        <p className="truncate text-sm text-gray-400">
          {assistant.description || "No description provided"}
        </p>
      </div>

      <div className="hidden flex-shrink-0 items-center gap-1.5 md:flex">
        <MetaChip>{model}</MetaChip>
        <MetaChip>{mode}</MetaChip>
      </div>

      <span className="hidden flex-shrink-0 items-center gap-1.5 text-xs text-gray-500 lg:flex">
        <Clock className="h-3.5 w-3.5" />
        {formatDate(updatedAtOf(assistant))}
      </span>

      <AgentCardMenu assistant={assistant} onChatClick={onChatClick} onDelete={onDelete} className="flex-shrink-0" />
    </div>
  );
}));

AssistantCard.displayName = 'AssistantCard';

export default function AssistantsView({ projectId, onEditingChange, initialCreating = false, hideTitle = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Context y hooks
  // Kept because several handlers still reset it; the grid no longer dims
  // non-hovered cards, so nothing reads the value.
  // eslint-disable-next-line no-unused-vars
  const [hoveredAssistantId, setHoveredAssistantId] = useState(null);
  // Estado para controlar el projectId actual y evitar renders con datos del proyecto anterior
  const [currentProjectId, setCurrentProjectId] = useState(projectId);
  const {
    assistants,
    assistantsTotal,
    assistantsHasMore,
    loadingMoreAssistants,
    loadMoreAssistants,
    loading: assistantsLoading,
    // eslint-disable-next-line no-unused-vars
    error: assistantsError,
    // eslint-disable-next-line no-unused-vars
    selectedAssistant,
    // eslint-disable-next-line no-unused-vars
    setSelectedAssistant,
    loadAssistants,
    createAssistant,
    updateAssistant,
    deleteAssistant,
    getAssistant,
    templates: contextTemplates,
    llmModels,
  } = useAssistantContext();

  // Estado local para templates/avatars refrescables
  const [templates, setTemplates] = useState(contextTemplates || []);
  
  // Estado para skills disponibles desde la API
  const [availableSkills, setAvailableSkills] = useState([]);
  const [integrationSnapshot, setIntegrationSnapshot] = useState({ meta: [], shopify: [], google: [] });
  const [checkingIntegrations, setCheckingIntegrations] = useState(false);
  const [createDependenciesLoading, setCreateDependenciesLoading] = useState(false);
  
  const { user } = useAuth();
  const { currentAccount, projects = [] } = useOrganization();
  const accountId = currentAccount?.id;
  const activeAccountId = currentAccount?.id || user?.account_id || user?.accountId || user?.sub || null;
  // eslint-disable-next-line no-unused-vars
  const userAvatar = user?.avatar_url || "/placeholder-user.png";

  const filterVisibleTemplates = useCallback((templateList) => {
    const safeTemplates = Array.isArray(templateList) ? templateList : [];

    if (!activeAccountId) {
      return [];
    }

    return safeTemplates.filter((template) => {
      const templateAccountId =
        template?.account_id ||
        template?.accountId ||
        template?.account?.id ||
        template?.owner_account_id ||
        null;

      if (!templateAccountId) {
        return false;
      }

      return String(templateAccountId) === String(activeAccountId);
    });
  }, [activeAccountId]);

  const getPreferredTemplateId = useCallback((templateList) => {
    const safeTemplates = Array.isArray(templateList) ? templateList : [];

    if (safeTemplates.some((template) => template.id === DEFAULT_TEMPLATE_ID)) {
      return DEFAULT_TEMPLATE_ID;
    }

    return safeTemplates[0]?.id || NO_AVATAR_TEMPLATE_ID;
  }, []);

  const loadIntegrationSnapshot = useCallback(async () => {
    if (!activeAccountId) {
      setIntegrationSnapshot({ meta: [], shopify: [], google: [] });
      return { meta: [], shopify: [], google: [] };
    }

    setCheckingIntegrations(true);
    try {
      const integrationsResult = await IntegrationService.listAllIntegrations(activeAccountId, true);
      if (integrationsResult.success && integrationsResult.data) {
        const snapshot = {
          meta: Array.isArray(integrationsResult.data.meta) ? integrationsResult.data.meta : [],
          shopify: Array.isArray(integrationsResult.data.shopify) ? integrationsResult.data.shopify : [],
          google: Array.isArray(integrationsResult.data.google) ? integrationsResult.data.google : [],
        };
        setIntegrationSnapshot(snapshot);
        return snapshot;
      }
    } catch (integrationError) {
      console.error("Failed to load integrations snapshot", integrationError);
    } finally {
      setCheckingIntegrations(false);
    }

    const emptySnapshot = { meta: [], shopify: [], google: [] };
    setIntegrationSnapshot(emptySnapshot);
    return emptySnapshot;
  }, [activeAccountId]);

  const loadCreateDependencies = useCallback(async () => {
    setCreateDependenciesLoading(true);

    // The account-scoped endpoint already returns what this account may see
    // (public + its own private), ordered — no client-side account filter.
    let visibleTemplates = [];
    setTemplates(visibleTemplates);

    try {
      const [templatesResult, skillsResult] = await Promise.allSettled([
        AssistantService.listTemplates({ view: 'summary', accountId: activeAccountId }),
        AssistantService.getSkills(),
        loadIntegrationSnapshot(),
      ]);

      if (
        templatesResult.status === 'fulfilled' &&
        templatesResult.value?.success &&
        Array.isArray(templatesResult.value.data)
      ) {
        visibleTemplates = templatesResult.value.data;
      }

      if (
        skillsResult.status === 'fulfilled' &&
        skillsResult.value?.success &&
        Array.isArray(skillsResult.value.data?.skills)
      ) {
        setAvailableSkills(skillsResult.value.data.skills);
      } else {
        setAvailableSkills([]);
      }

      setTemplates(visibleTemplates);
      return visibleTemplates;
    } catch {
      setTemplates(visibleTemplates);
      setAvailableSkills([]);
      return visibleTemplates;
    } finally {
      setCreateDependenciesLoading(false);
    }
  }, [activeAccountId, loadIntegrationSnapshot]);
  
  // Load create dependencies immediately when mounted in create mode
  useEffect(() => {
    if (initialCreating) loadCreateDependencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toast notifications
  // eslint-disable-next-line no-unused-vars
  const { toasts, removeToast, success, error, warning, info } = useToast();
  
  // Hook de Zeno para el tab de chat
  const { 
    // eslint-disable-next-line no-unused-vars
    messages: zenoMessages, 
    // eslint-disable-next-line no-unused-vars
    sending: zenoSending, 
    chatWithZenoStream 
  } = useZeno();

    // --- Sidebar historial de chats ---
    const [showSidebar, setShowSidebar] = useState(false);
    const [chatHistories, setChatHistories] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [chatHistoryPage, setChatHistoryPage] = useState(0); // For pagination
    const CHAT_HISTORY_PAGE_SIZE = 5;
    // eslint-disable-next-line no-unused-vars
    const [loadingHistories, setLoadingHistories] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [selectedHistory, setSelectedHistory] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [historyMessages, setHistoryMessages] = useState([]);
    const [hasScrollableHistories, setHasScrollableHistories] = useState(false);
    const totalPages = Math.max(1, Math.ceil(chatHistories.length / CHAT_HISTORY_PAGE_SIZE));

    // Abrir sidebar y cargar historiales
    const fetchAllChatHistories = async () => {
      if (!accountId && !user?.user_id) return;
      if (!chattingAssistant?.id) return;
      
      setLoadingHistories(true);
      try {
        // Usar el nuevo método que filtra por assistant específico
        const result = await AssistantService.listChatHistoriesByAssistant({
          accountId,
          assistantId: chattingAssistant.id,
          limit: 15,
          offset: 0,
          sort_by: 'updated_at',
          descending: true
        });
        if (result.success && Array.isArray(result.data)) {
          setChatHistories(result.data);
        } else {
          setChatHistories([]);
        }
      } catch (err) {
        setChatHistories([]);
        console.error('Error fetching chat histories for assistant:', err);
      } finally {
        setLoadingHistories(false);
      }
    };

    const openSidebar = async () => {
      setShowSidebar(true);
      setChatHistoryPage(0);
      await fetchAllChatHistories();
    };

    // Detectar si hay contenido scrolleable en el historial
    useEffect(() => {
      if (!chatHistories.length) {
        setHasScrollableHistories(false);
        return;
      }

      const checkScrollable = () => {
        const scrollContainer = document.getElementById("chat-history-scroll-chat");
        if (scrollContainer) {
          const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;
          setHasScrollableHistories(hasScroll);
        }
      };

      // Verificar inmediatamente y después de un delay para asegurar el render
      checkScrollable();
      const timeoutId = setTimeout(checkScrollable, 50);
      
      return () => clearTimeout(timeoutId);
    }, [chatHistories.length]); // Solo depender del length, no del array completo

    // eslint-disable-next-line no-unused-vars
    const handleChatHistoryPageChange = (newPage) => {
      if (newPage >= 0 && newPage < totalPages) {
        setChatHistoryPage(newPage);
      }
    };

    // Cerrar sidebar
    // eslint-disable-next-line no-unused-vars
    const closeSidebar = () => {
      setShowSidebar(false);
      setSelectedHistory(null);
      setHistoryMessages([]);
    };

    // Seleccionar historial y cargar mensajes
    const handleSelectHistory = async (history) => {
      setLoadingHistories(true);
      setShowInputAnimation(false); // Resetear animación al cargar un chat existente
      try {
        // Solo cargar los mensajes del historial seleccionado, filtrando los de role 'system'
        const messagesResult = await ChatService.getChatMessages(history.id);
        if (messagesResult.success && Array.isArray(messagesResult.data)) {
          const filteredMessages = messagesResult.data.filter(msg => msg.role !== 'system');
          setChatMessages(filteredMessages);
          setChatHistoryId(history.id);
        } else {
          setChatMessages([]);
        }
      } catch (err) {
        setChatMessages([]);
      } finally {
        setLoadingHistories(false);
      }
    };

    // Volver al listado de historiales
    // eslint-disable-next-line no-unused-vars
    const handleBackToList = () => {
      setSelectedHistory(null);
      setHistoryMessages([]);
    };
  const [viewMode, setViewMode] = useState("grid")
  // List controls (mirrors the Avatars view: search + a status segmented filter).
  const [searchQuery, setSearchQuery] = useState("")
  // eslint-disable-next-line no-unused-vars
  const [statusFilter, setStatusFilter] = useState("all") // filtro Live/Draft retirado; se deja inerte en "all"
  // Filtro por proyecto (solo en la vista global; en ProjectDetail el scope ya
  // viene por el prop `projectId`). Recarga server-side: pasa project_id al query,
  // no filtra la lista account-wide en el cliente.
  // Filtro por proyecto (solo vista global). Vive en la URL (?projectId) para que
  // el overview del proyecto pueda deep-linkear directo a "sus" agentes.
  const urlProjectId = searchParams.get("projectId") || ""
  const handleProjectFilter = (pid) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (pid) next.set("projectId", pid)
      else next.delete("projectId")
      return next
    }, { replace: true })
  }

  // Global: recarga server-side cuando cambia ?projectId. En modo in-project manda
  // el prop `projectId`, no la URL.
  useEffect(() => {
    if (projectId) return
    setCurrentProjectId(urlProjectId || null)
    loadAssistants(urlProjectId ? { projectId: urlProjectId } : {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProjectId, projectId])
  const agentsSentinelRef = useRef(null)
  const [creating, setCreating] = useState(initialCreating)
  const [creatingAvatarFlow, setCreatingAvatarFlow] = useState(false)
  const [createStep, setCreateStep] = useState(0)
  // Refrescar templates y skills al abrir el modal de crear assistant
  const handleOpenCreate = () => {
    const visibleTemplates = filterVisibleTemplates(contextTemplates || []);

    resetForm(getPreferredTemplateId(visibleTemplates));
    setTemplates(visibleTemplates);
    setCreating(true);
    loadCreateDependencies();
  };
  const [editing, setEditing] = useState(false) // Nuevo estado para edición
  const [editingAssistant, setEditingAssistant] = useState(null) // Assistant siendo editado
  const [tab, setTab] = useState("configure")
  const [submitting, setSubmitting] = useState(false)
  
  // Notify parent when editing or creating state changes
  useEffect(() => {
    if (onEditingChange) {
      onEditingChange(editing || creating || creatingAvatarFlow);
    }
  }, [editing, creating, creatingAvatarFlow, onEditingChange]);
  
  // Confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [assistantToDelete, setAssistantToDelete] = useState(null)

  // form state
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [selectedModel, setSelectedModel] = useState("b7cad7e6-7369-4a1a-a3e1-1923615d6ccc") // GPT-4o Mini por defecto
  const [selectedTemplate, setSelectedTemplate] = useState("00000000-0000-0000-0000-000000000001") // General por defecto
  // Proyecto elegido al crear desde la vista global (sin prop `projectId`). Un agente
  // no puede quedar sin proyecto: en modo in-project lo fija el contexto, en global
  // el usuario lo elige (default heredado del avatar).
  const [selectedProjectId, setSelectedProjectId] = useState("")
  const [language, setLanguage] = useState("es")
  const [selectedSkills, setSelectedSkills] = useState([]) // Array de skills seleccionados
  const [skillSettings, setSkillSettings] = useState({}) // Per-skill config (image_generation allowed_models/default_model)
  const [skillIntegrations, setSkillIntegrations] = useState({}) // Map skill name → integration UUID (per-skill credential binding)
  const [executionMode, setExecutionMode] = useState(DEFAULT_EXECUTION_MODE)
  const [responseFormat, setResponseFormat] = useState("text")
  const [temperature, setTemperature] = useState(100) // 1.0 por defecto
  const [maxTokens, setMaxTokens] = useState(2048)
  const [memoryEnabled, setMemoryEnabled] = useState(false)
  const [acceptsFilesAndImages, setAcceptsFilesAndImages] = useState(false) // Permite imágenes y archivos
  const [isDraft, setIsDraft] = useState(false)
  const [avatarBase64, setAvatarBase64] = useState("")
  const [avatarUrl, setAvatarUrl] = useState("")
  const [validationErrors, setValidationErrors] = useState({})
  const [sections, setSections] = useState([]); // {id,title,html,markdown}

  const missingSkillIntegrations = useMemo(
    () => getMissingSkillIntegrations(selectedSkills, availableSkills, integrationSnapshot),
    [selectedSkills, availableSkills, integrationSnapshot]
  );

  const persistCreateDraftForReturn = useCallback((reason = "manual") => {
    const payload = {
      reason,
      createdAt: Date.now(),
      draft: {
        createStep,
        name,
        description,
        selectedModel,
        selectedTemplate,
        language,
        selectedSkills,
        executionMode,
        responseFormat,
        temperature,
        maxTokens,
        memoryEnabled,
        acceptsFilesAndImages,
        isDraft,
        avatarBase64,
        avatarUrl,
        sections,
      },
    };

    sessionStorage.setItem(ASSISTANT_CREATE_DRAFT_KEY, JSON.stringify(payload));
  }, [
    avatarBase64,
    avatarUrl,
    createStep,
    description,
    executionMode,
    isDraft,
    language,
    maxTokens,
    memoryEnabled,
    acceptsFilesAndImages,
    name,
    responseFormat,
    sections,
    selectedModel,
    selectedSkills,
    selectedTemplate,
    temperature,
  ]);

  useEffect(() => {
    setTemplates(filterVisibleTemplates(contextTemplates || []));
  }, [contextTemplates, filterVisibleTemplates]);

  useEffect(() => {
    if (creating || editing || creatingAvatarFlow) return;

    const rawDraft = sessionStorage.getItem(ASSISTANT_CREATE_DRAFT_KEY);
    if (!rawDraft) return;

    try {
      const parsedDraft = JSON.parse(rawDraft);
      const isExpired =
        !parsedDraft?.createdAt ||
        Date.now() - Number(parsedDraft.createdAt) > ASSISTANT_CREATE_DRAFT_MAX_AGE_MS;

      if (isExpired || !parsedDraft?.draft) {
        sessionStorage.removeItem(ASSISTANT_CREATE_DRAFT_KEY);
        return;
      }

      const restored = parsedDraft.draft;

      setCreateStep(Number.isInteger(restored.createStep) ? restored.createStep : 0);
      setName(restored.name || "");
      setDescription(restored.description || "");
      setSelectedModel(restored.selectedModel || "b7cad7e6-7369-4a1a-a3e1-1923615d6ccc");
      setSelectedTemplate(restored.selectedTemplate || DEFAULT_TEMPLATE_ID);
      setLanguage(restored.language || "es");
      setSelectedSkills(Array.isArray(restored.selectedSkills) ? restored.selectedSkills : []);
      setExecutionMode(restored.executionMode || DEFAULT_EXECUTION_MODE);
      setResponseFormat(restored.responseFormat || "text");
      setTemperature(Number.isFinite(restored.temperature) ? restored.temperature : 100);
      setMaxTokens(Number.isFinite(restored.maxTokens) ? restored.maxTokens : 2048);
      setMemoryEnabled(restored.memoryEnabled !== false);
      setIsDraft(Boolean(restored.isDraft));
      setAvatarBase64(restored.avatarBase64 || "");
      setAvatarUrl(restored.avatarUrl || "");
      setSections(Array.isArray(restored.sections) ? restored.sections : []);
      setValidationErrors({});

      setCreating(true);

      sessionStorage.removeItem(ASSISTANT_CREATE_DRAFT_KEY);
      loadCreateDependencies();
    } catch {
      sessionStorage.removeItem(ASSISTANT_CREATE_DRAFT_KEY);
    }
  }, [creating, editing, creatingAvatarFlow, loadCreateDependencies]);

  useEffect(() => {
    if (!creating) return;

    const validTemplateIds = new Set((templates || []).map((template) => template.id));
    if (selectedTemplate === NO_AVATAR_TEMPLATE_ID) return;

    if (!validTemplateIds.has(selectedTemplate)) {
      setSelectedTemplate(getPreferredTemplateId(templates));
    }
  }, [creating, templates, selectedTemplate, getPreferredTemplateId]);

  // Default the project from the chosen avatar's live project (the template row,
  // not a stale config snapshot). Only in global create mode — in-project mode the
  // project is fixed by the view. Leaves the selection untouched when the avatar
  // has no project, so the user can still pick one.
  useEffect(() => {
    if (!creating || projectId) return;
    const tpl = (templates || []).find((t) => t.id === selectedTemplate);
    const tplProject = tpl?.projectId || tpl?.project_id;
    if (tplProject) setSelectedProjectId(tplProject);
  }, [creating, projectId, selectedTemplate, templates]);

  // Refs for scrolling to validation errors
  const nameInputRef = useRef(null);
  const executionModeRef = useRef(null);

  // Chat UI states
  const [chattingAssistant, setChattingAssistant] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatAttachments, setChatAttachments] = useState([]);
  const [chatSending, setChatSending] = useState(false);
  const [chatHistoryId, setChatHistoryId] = useState(null);
  const [chatIsStreaming, setChatIsStreaming] = useState(false);
  const [showInputAnimation, setShowInputAnimation] = useState(false);
  const [contentBudget, setContentBudget] = useState(null);
  const budgetFromErrorRef = useRef(false);
  const chatStreamControllerRef = useRef(null);
  const chatScrollContainerRef = useRef(null);
  const chatMessagesEndRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const chatInputAreaRef = useRef(null); // Ref para el área del input

  // Auto-scroll del chat cuando se agregan nuevos mensajes
  useEffect(() => {
    const container = chatScrollContainerRef.current;
    const inputArea = chatInputAreaRef.current;
    
    if (!inputArea) {
      return;
    }

    // Si hay mensajes, hacer scroll dentro del contenedor de mensajes
    if (container && chatMessages.length > 0) {
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }, 200);
    }
    
    // SIEMPRE hacer scroll de la ventana para mostrar el input
    setTimeout(() => {
      inputArea.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'end',
        inline: 'nearest'
      });
    }, 300);
  }, [chatMessages, chatIsStreaming]);

  // Activar animación de caída del input cuando se envía el primer mensaje
  useEffect(() => {
    if (chatMessages.length === 1) {
      setShowInputAnimation(true);
    }
  }, [chatMessages.length]);

  // Agregar estilos de scrollbar personalizados
  useEffect(() => {
    const scrollbarStyles = `
      .message-bubble-user::-webkit-scrollbar {
        width: 4px;
      }
      .message-bubble-user::-webkit-scrollbar-track {
        background: transparent;
      }
      .message-bubble-user::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 10px;
        border: none;
      }
      .message-bubble-user::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
      }
      .message-bubble-user::-webkit-scrollbar-corner {
        background: transparent;
      }
      
      .message-bubble-assistant::-webkit-scrollbar {
        width: 4px;
      }
      .message-bubble-assistant::-webkit-scrollbar-track {
        background: transparent;
      }
      .message-bubble-assistant::-webkit-scrollbar-thumb {
        background: rgba(156, 163, 175, 0.4);
        border-radius: 10px;
        border: none;
      }
      .message-bubble-assistant::-webkit-scrollbar-thumb:hover {
        background: rgba(156, 163, 175, 0.6);
      }
      .message-bubble-assistant::-webkit-scrollbar-corner {
        background: transparent;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .animate-fade-in {
        animation: fadeIn 0.3s ease-out;
      }
    `;

    // Agregar estilos al head si no existen
    if (!document.getElementById('message-scrollbar-styles')) {
      const style = document.createElement('style');
      style.id = 'message-scrollbar-styles';
      style.textContent = scrollbarStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Función para prevenir el scroll del contenedor principal cuando el mensaje llega a sus límites
  const handleMessageScroll = (e) => {
    const element = e.target;
    const { scrollTop, scrollHeight, clientHeight } = element;
    const deltaY = e.deltaY;

    // Si está intentando hacer scroll hacia abajo y ya está en el fondo
    if (deltaY > 0 && scrollTop + clientHeight >= scrollHeight) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Si está intentando hacer scroll hacia arriba y ya está en la parte superior
    if (deltaY < 0 && scrollTop <= 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  // Función para prevenir scroll de la página cuando mouse está sobre contenedor del chat
  const handleChatContainerScroll = (e) => {
    const target = e.target;
    const currentElement = e.currentTarget;
    
    // Si el target es el propio contenedor (no un elemento scrolleable interno), prevenir
    if (target === currentElement || target.scrollHeight <= target.clientHeight) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Cargar assistants filtrados por proyecto
  useEffect(() => {
    if (projectId && projectId !== currentProjectId) {
      // Actualizar el projectId actual inmediatamente
      setCurrentProjectId(projectId);
      
      // Limpiar estados locales al cambiar de proyecto
      setChattingAssistant(null);
      setChatMessages([]);
      setChatHistoryId(null);
      setEditing(false);
      setEditingAssistant(null);
      setCreating(false);
      setHoveredAssistantId(null); // Reset hover state
      
      // Cargar assistants del nuevo proyecto
      loadAssistants({ projectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Reset hover state when assistants change to prevent sticky hover
  useEffect(() => {
    setHoveredAssistantId(null);
  }, [assistants]);

  // Project scope + status filter + free-text search, all client-side over the
  // pages loaded so far.
  const filteredAssistants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return assistants.filter((assistant) => {
      if (currentProjectId && assistant.projectId !== currentProjectId) return false;
      if (statusFilter === "draft" && !assistant.isDraft) return false;
      if (statusFilter === "live" && assistant.isDraft) return false;
      if (query) {
        const matches =
          assistant.name?.toLowerCase().includes(query) ||
          assistant.description?.toLowerCase().includes(query) ||
          modelOf(assistant)?.toLowerCase().includes(query);
        if (!matches) return false;
      }
      return true;
    });
  }, [assistants, currentProjectId, statusFilter, searchQuery]);

  // Summary counts. `total` is exact (the API returns it); the rest are derived
  // from the pages loaded so far, hence the `approx` flag while more remain.
  const agentStats = useMemo(() => {
    const models = {};
    assistants.forEach((a) => {
      const m = modelOf(a);
      if (m) models[m] = (models[m] || 0) + 1;
    });
    const topModel = Object.entries(models).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    return {
      total: assistantsTotal || assistants.length,
      live: assistants.filter((a) => !a.isDraft).length,
      drafts: assistants.filter((a) => a.isDraft).length,
      withSkills: assistants.filter((a) => skillsOf(a).length > 0).length,
      topModel,
    };
  }, [assistants, assistantsTotal]);

  // Infinite scroll — pull the next page as the sentinel nears the viewport.
  useEffect(() => {
    const node = agentsSentinelRef.current;
    if (!node || !assistantsHasMore || assistantsLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreAssistants();
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [assistantsHasMore, assistantsLoading, loadMoreAssistants]);

  /**
   * Validar formulario antes de crear assistant
   */
  const validateForm = () => {
    const errors = {
      name: !name.trim(),
      // An agent must belong to a project. In-project mode the prop fixes it; in
      // global mode the user must pick one (defaults to the avatar's project).
      project: !projectId && !selectedProjectId,
      toolUsing: executionMode === 'tool_using' && selectedSkills.length === 0,
      missingIntegrations: missingSkillIntegrations.length > 0,
    };
    
    setValidationErrors(errors);
    
    // Scroll to first error
    if (errors.name && nameInputRef.current) {
      nameInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    return !Object.values(errors).some(error => error);
  };

  /**
   * Limpiar errores de validación cuando se modifican los campos
   */
  const clearValidationError = (field) => {
    setValidationErrors(prev => ({ ...prev, [field]: false }));
  };

  /**
   * Manejar creación de assistant con el servicio real
   */
  const handleCreate = async () => {
    if (missingSkillIntegrations.length > 0) {
      setCreateStep(2);
      warning(
        'Missing Integrations',
        'Connect the required integrations for your selected skills before creating the agent.'
      );
      return;
    }

    if (!validateForm()) {
      error('Validation Error', 'Please fix the highlighted fields before continuing.');
      return;
    }

    setSubmitting(true);

    try {
      // Obtener el modelo seleccionado
      const selectedLLM = llmModels.find(m => m.id === selectedModel) || llmModels[0];
      
      // Crear assistant usando la estructura exacta de la documentación API
      const assistantData = {
        name: name.trim(),
        description: description.trim() || `AI Agent created on ${new Date().toLocaleDateString()}`,
        type: "general", // "general" | "built_in" (built_in requiere subtype)
        skills: selectedSkills, // Enviar el array de skills directamente
        avatar: (avatarBase64 && avatarBase64.trim() !== '') ? avatarBase64 : (avatarUrl && avatarUrl.trim() !== '') ? avatarUrl : null,
        reactAgentId: null,
        memory: "",
        executionMode: executionMode,
        projectId: projectId || selectedProjectId,
        config: {
          llm_id: selectedModel,
          model_label: selectedLLM?.name || "GPT-4o Mini",
          assistant_template: {
            id: selectedTemplate,
            name: templates.find(t => t.id === selectedTemplate)?.name || "General Agent",
            description: "A versatile general-purpose agent template",
            is_default: selectedTemplate === "00000000-0000-0000-0000-000000000001",
            sections: sections.map((section, index) => ({
              id: `section-${index}`,
              template_id: selectedTemplate,
              name: section.name || `Section ${index + 1}`,
              order: index + 1,
              content: section.content || ""
            }))
          },
          template_id: selectedTemplate,
          skills: selectedSkills, // Enviar el array de skills directamente
          skill_settings: pruneSkillSettings(skillSettings, selectedSkills),
          skill_integrations: pruneSkillIntegrations(skillIntegrations, selectedSkills),
          temperature: temperature / 100, // Convertir de 0-100 a 0-1
          max_tokens: maxTokens,
          root_prompt: "", // Se puede usar para prompts personalizados
          reasoning_effort: "", // Campo opcional para configuración de razonamiento
          memory_enabled: memoryEnabled,
          accepts_files_and_images: acceptsFilesAndImages, // Flag para habilitar archivos e imágenes
          is_active: true,
          isDraft: isDraft
        }
      };

      const result = await createAssistant(assistantData);

      if (result.success) {
        success('Created', `${name} created successfully`);
        // Resetear formulario
        resetForm();
        setCreating(false);
      } else {
        error('Creation Failed', result.error.message || 'Failed to create agent');
      }
      return result;
    } catch (error) {
      error('Unexpected Error', 'An unexpected error occurred while creating the agent');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Manejar actualización de assistant
   */
  const handleUpdate = async (assistantId, updatedData) => {
    // Validar que el assistant existe antes de intentar actualizarlo
    if (!assistantId) {
      error('Invalid Agent', 'Agent ID is missing');
      return;
    }

    setSubmitting(true);

    try {
      console.log('🔍 Verificando que el assistant existe antes de actualizar...');
      
      // Verificar que el assistant aún existe
      const existsResult = await getAssistant(assistantId);
      
      if (!existsResult.success) {
        error('Agent No Encontrado', 'Este agent puede haber sido eliminado. Actualizando la lista...');
        
        // Recargar la lista para sincronizar con el servidor
        await loadAssistants({ projectId });
        
        setEditing(false);
        setEditingAssistant(null);
        return;
      }

      // Agregar una segunda verificación justo antes del update para detectar problemas de timing
      const secondCheck = await getAssistant(assistantId);
      
      if (!secondCheck.success) {
        error('Agent Deleted', 'The agent was deleted during editing. Updating list...');
        await loadAssistants({ projectId });
        setEditing(false);
        setEditingAssistant(null);
        return;
      }
      
      const result = await updateAssistant(assistantId, updatedData);

      if (result.success) {
        success('Updated', 'Agent updated successfully');
        
        // Actualizar el assistant en el estado local
        setEditingAssistant(result.data);
        
        // Recargar la lista de assistants
        await loadAssistants();
        
        // Volver a la vista principal
        setEditing(false);
        setEditingAssistant(null);
      } else {
        console.error('❌ Error en la actualización:', result.error);
        
        // Log detailed error information for 422 validation errors
        if (result.error?.status === 422) {
          
          // Si hay errores específicos de validación, mostrarlos
          if (result.error?.message && Array.isArray(result.error.message)) {
            const validationErrors = result.error.message.map(err => err.message || err).join(', ');
            error('Validation Errors', `The following fields require attention: ${validationErrors}`);
          } else if (result.error?.errors) {
            const errorMessages = Object.values(result.error.errors).flat().join(', ');
            error('Validation Errors', errorMessages);
          } else if (result.error?.message) {
            error('Validation Error', result.error.message);
          } else {
            error('Validation Error', 'Agent data contains validation errors. Please review all required fields.');
          }
        }
        // Manejar errores específicos
        else if (result.error.status === 404) {
          error('Agent No Encontrado', 'Este agent ya no existe en el servidor. Actualizando la lista...');
          
          // Recargar la lista para sincronizar con el servidor
          await loadAssistants({ projectId });
          
          setEditing(false);
          setEditingAssistant(null);
        } else {
          error('Update Error', result.error.message || 'Could not update the agent');
        }
      }
      return result;
    } catch (error) {
      console.error('💥 Error inesperado durante la actualización:', error);
      error('Unexpected Error', 'An unexpected error occurred while updating the agent');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Manejar eliminación de assistant
   */
  const handleDelete = useCallback((assistantId) => {
    setAssistantToDelete(assistantId);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = async () => {
    if (!assistantToDelete) return;

    const result = await deleteAssistant(assistantToDelete, 'User deleted assistant');
    
    if (result.success) {
      success('Deleted', 'Agent deleted successfully');
    } else {
      console.error('❌ Error eliminando assistant:', result.error);
      error('Deletion Failed', result.error.message || 'Failed to delete agent');
    }
    
    setShowDeleteConfirm(false);
    setAssistantToDelete(null);
  };

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setAssistantToDelete(null);
  }, []);

  /**
   * Manejar click en assistant para obtener detalles
   * Usa GET /assistants/{assistant_id}
   */
  // Click en una card → abre el agent workspace en la sección chat (deep-link por
  // ?agentId + ?section). El workspace ya trae Settings para configurar el agente.
  const openAgentWorkspace = useCallback((assistant) => {
    const projectId = assistant.projectId || assistant.project_id;
    if (!projectId) {
      error("Can't open agent", "This agent isn't linked to a project yet.");
      return;
    }
    navigate(`/dashboard?view=Projects&projectId=${projectId}&tab=assistants&agentId=${assistant.id}&section=chat&from=agents`);
  }, [navigate, error]);

  // eslint-disable-next-line no-unused-vars
  const handleSelectAssistant = async (assistantId) => {
    // Limpiar hover state al seleccionar assistant
    setHoveredAssistantId(null);
    
    try {
      // Always fetch the full assistant for editing — the list endpoint returns a summary
      // that omits config fields like temperature, maxTokens, acceptsFilesAndImages, etc.
      const result = await getAssistant(assistantId);

      if (!result.success) {
        console.error('❌ Error obteniendo assistant:', result.error);
        error('Load Failed', result.error.message || 'Failed to load agent');
        return;
      }



      const assistant = result.data;
      const config = assistant.config || {};
      const selectedModelId = config.llmId || config.llm_id || 'b7cad7e6-7369-4a1a-a3e1-1923615d6ccc';
      const selectedTemplateId = config.templateId || config.template_id || '00000000-0000-0000-0000-000000000001';
      const skillsRoot = Array.isArray(assistant.skills) ? assistant.skills : [];
      const skillsConfig = Array.isArray(config.skills) ? config.skills : [];
      const allSkills = skillsRoot.length > 0 ? skillsRoot : skillsConfig;
      const assistantTemplate = config.assistantTemplate || config.assistant_template;

      setName(assistant.name || '');
      setDescription(assistant.description || '');
      setSelectedModel(selectedModelId);
      setSelectedTemplate(selectedTemplateId);
      setSelectedSkills(allSkills.filter((skill) => skill !== 'memory'));
      setTemperature(Math.round((config.temperature ?? 1) * 100));
      setMaxTokens(config.maxTokens ?? config.max_tokens ?? 2048);
      setMemoryEnabled(config.memoryEnabled ?? config.memory_enabled ?? true);
      setSections(assistantTemplate?.sections || []);

      setEditingAssistant(assistant);
      setEditing(true);
      success('Loaded', `${assistant.name} ready for editing`);
    } catch (error) {
      console.error('Error inesperado:', error);
      error('Unexpected Error', 'An unexpected error occurred while loading the agent');
    }
  };

  /**
   * Manejar envío de mensaje a Zeno
   */
  // eslint-disable-next-line no-unused-vars
  const handleSendToZeno = async (message) => {
    await chatWithZenoStream(message);
  };

  /**
   * Resetear formulario a valores por defecto
   */
  const resetForm = (preferredTemplateId = DEFAULT_TEMPLATE_ID) => {
    sessionStorage.removeItem(ASSISTANT_CREATE_DRAFT_KEY);
    setName("");
    setDescription("");
    setSelectedModel("b7cad7e6-7369-4a1a-a3e1-1923615d6ccc");
    setSelectedTemplate(preferredTemplateId);
    setSelectedProjectId("");
    setLanguage("es");
    setSelectedSkills([]);
    setSkillSettings({});
    setSkillIntegrations({});
    setExecutionMode(DEFAULT_EXECUTION_MODE);
    setResponseFormat("text");
    setTemperature(100);
    setMaxTokens(2048);
    setMemoryEnabled(true);
    setIsDraft(false);
    setSections([]);
    setAvatarBase64("");
    setAvatarUrl("");
    setCreateStep(0);
  };

  const refreshAssistantTemplates = async () => {
    let visibleTemplates = filterVisibleTemplates(contextTemplates || []);

    try {
      const result = await AssistantService.listTemplates({ view: 'summary' });
      if (result.success && Array.isArray(result.data)) {
        visibleTemplates = filterVisibleTemplates(result.data);
      }
    } catch (e) {
      visibleTemplates = filterVisibleTemplates(contextTemplates || []);
    }

    setTemplates(visibleTemplates);
    await loadIntegrationSnapshot();
    return visibleTemplates;
  };

  const handleCreateAvatarFromWizard = () => {
    setCreatingAvatarFlow(true);
  };

  const handleGoToIntegrations = () => {
    persistCreateDraftForReturn("missing_skill_integrations");
    navigate("/dashboard?view=Integrations");
  };

  const handleReturnFromAvatarFlow = async (createdTemplate) => {
    const visibleTemplates = await refreshAssistantTemplates();

    if (createdTemplate?.id) {
      const exists = visibleTemplates.some((template) => template.id === createdTemplate.id);
      if (exists) {
        setSelectedTemplate(createdTemplate.id);
      }
    }

    setCreatingAvatarFlow(false);
  };

  /**
   * Volver a la lista desde la vista de edición
   */
  const handleBackFromEditing = () => {
    setEditing(false);
    setEditingAssistant(null);
    // Limpiar hover state al salir del configure
    setHoveredAssistantId(null);
    // Reset form usando la función común
    resetForm();
  };

  // Abrir chat con assistant (sin crear chat history automáticamente)
  const handleOpenChat = useCallback(async (assistant) => {
    setChattingAssistant(assistant);
    setChatMessages([]);
    setChatInput("");
    setChatAttachments([]);
    setChatHistoryId(null); // No crear chat history hasta que se envíe el primer mensaje

    // Fetch full assistant detail to get llmContextWindow and rootPrompt for client-side budget estimation
    if (assistant?.id) {
      AssistantService.getAssistant(assistant.id).then(result => {
        if (result.success && result.data) {
          setChattingAssistant(prev => prev && prev.id === result.data.id ? {
            ...prev,
            llmContextWindow: result.data.llmContextWindow ?? result.data.llm_context_window,
            config: { ...(prev.config || {}), ...(result.data.config || {}) },
          } : prev);
        }
      }).catch(() => {});
    }

    // Scroll para centrar la interfaz de chat en pantalla
    setTimeout(() => {
      const chatPanel = document.querySelector('.bg-\\[\\#18181b\\].border-white\\/20.text-white.w-full.max-w-7xl.mx-auto.rounded-2xl.shadow-2xl.flex.flex-row.overflow-hidden.mb-8.h-\\[800px\\]');
      if (chatPanel && typeof chatPanel.scrollIntoView === 'function') {
        chatPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }, []);

  const formatAttachmentSize = (size) => {
    if (!Number.isFinite(size) || size <= 0) return '0 B';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

  const readFileAsArrayBuffer = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsArrayBuffer(file);
  });

  // Re-estimate content budget whenever attachments change
  useEffect(() => {
    // Skip re-estimation if budget was just set from backend error recovery
    if (budgetFromErrorRef.current) {
      budgetFromErrorRef.current = false;
      return;
    }
    const validAttachments = chatAttachments.filter(att => !att.oversized && att.contentBlock);
    if (validAttachments.length === 0) {
      setContentBudget(null);
      return;
    }
    const contextWindow = chattingAssistant?.llmContextWindow || chattingAssistant?.llm_context_window;
    const rootPrompt = chattingAssistant?.config?.rootPrompt || '';
    const budget = estimateBudgetForAttachments(chatAttachments, contextWindow, chatMessages, rootPrompt);
    if (budget) {
      // Mark rejected attachments
      const rejectedFilenames = new Set(budget.rejected_blocks.map(b => b.filename));
      setChatAttachments(prev =>
        prev.map(att => ({
          ...att,
          budgetRejected: rejectedFilenames.has(att.name),
        }))
      );
      setContentBudget(budget);
    } else {
      // All fit or no context_window — clear any previous budget and rejected marks
      setChatAttachments(prev => prev.map(att => ({ ...att, budgetRejected: false })));
      setContentBudget(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatAttachments.length, chattingAssistant?.llmContextWindow, chattingAssistant?.llm_context_window, chatMessages.length]);

  const clearChatAttachments = () => {
    setChatAttachments([]);
    if (chatFileInputRef.current) {
      chatFileInputRef.current.value = '';
    }
  };

  const removeChatAttachment = (index) => {
    setChatAttachments(prev => prev.filter((_, i) => i !== index));
    // useEffect on chatAttachments.length will re-estimate budget
  };

  const handleAttachmentClick = () => {
    chatFileInputRef.current?.click();
  };

  const handleAttachmentSelected = async (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    const oversizedFiles = files.filter(file => file.size > MAX_CHAT_ATTACHMENT_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      error('Attachment too large', `${oversizedFiles.length} file(s) exceed 10 MB limit. Remove them to send your message.`);
    }

    // Add oversized files as rejected attachments
    const oversizedAttachments = oversizedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      previewUrl: null,
      contentBlock: null,
      oversized: true,
    }));

    const validFiles = files.filter(file => file.size <= MAX_CHAT_ATTACHMENT_SIZE_BYTES);

    if (validFiles.length === 0) {
      if (oversizedAttachments.length > 0) {
        setChatAttachments(prev => [...prev, ...oversizedAttachments]);
      }
      return;
    }

    try {
      const newAttachments = await Promise.all(
        validFiles.map(async (file, index) => {
          let contentBlock;
          let previewUrl = null;

          if (file.type.startsWith('image/')) {
            const dataUrl = await readFileAsDataUrl(file);
            previewUrl = dataUrl;
            contentBlock = {
              type: 'image',
              data: {
                url: dataUrl,
                mime_type: file.type,
                filename: file.name,
              },
              order: index + 1,
            };
          } else {
            const arrayBuffer = await readFileAsArrayBuffer(file);
            contentBlock = {
              type: 'file',
              data: {
                filename: file.name,
                mime_type: file.type || 'application/octet-stream',
                content: arrayBufferToBase64(arrayBuffer),
              },
              order: index + 1,
            };
          }

          return {
            name: file.name,
            size: file.size,
            mimeType: file.type || 'application/octet-stream',
            previewUrl,
            contentBlock,
          };
        })
      );

      setChatAttachments(prev => [...prev, ...oversizedAttachments, ...newAttachments]);
      // useEffect on chatAttachments.length will re-estimate budget
    } catch (attachmentError) {
      console.error('Error preparing chat attachments:', attachmentError);
      error('Attachment error', 'Some files could not be prepared for sending.');
    }
  };

  const buildUserContentBlocks = (messageText, attachments) => {
    const contentBlocks = [];
    const trimmedMessage = messageText.trim();

    if (trimmedMessage) {
      contentBlocks.push({
        type: 'text',
        data: { text: trimmedMessage },
        order: contentBlocks.length + 1,
      });
    }

    if (Array.isArray(attachments)) {
      attachments.forEach((attachment) => {
        if (attachment?.contentBlock) {
          contentBlocks.push({
            ...attachment.contentBlock,
            order: contentBlocks.length + 1,
          });
        }
      });
    }

    return contentBlocks;
  };

  const renderMessageContentBlocks = (message) => {
    if (!Array.isArray(message.contentBlocks) || message.contentBlocks.length === 0) {
      return null;
    }

    return message.contentBlocks.map((block, index) => {
      if (block.type === 'image' && block.data?.url) {
        return (
          <div key={`${message.id || 'message'}-image-${index}`} className="space-y-3">
            <img
              src={block.data.url}
              alt={block.data.filename || 'Attached image'}
              className="rounded-2xl max-w-full h-auto border border-white/10"
            />
            {block.data.filename && (
              <p className="text-xs text-gray-300">{block.data.filename}</p>
            )}
          </div>
        );
      }

      if (block.type === 'file') {
        return (
          <div
            key={`${message.id || 'message'}-file-${index}`}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
          >
            <p className="text-sm font-medium text-white truncate">{block.data?.filename || 'Attached file'}</p>
            <p className="text-xs text-gray-400 mt-1">{block.data?.mime_type || 'application/octet-stream'}</p>
          </div>
        );
      }

      const rawMarkdown = typeof block.data?.text === 'string'
        ? block.data.text
        : typeof block.data === 'string'
          ? block.data
          : JSON.stringify(block.data);

      const markdown = normalizeAssistantMarkdown(rawMarkdown);

      return markdown ? (
        <ReactMarkdown
          key={`${message.id || 'message'}-text-${index}`}
          remarkPlugins={[remarkGfm]}
          components={assistantMarkdownComponents}
        >
          {markdown}
        </ReactMarkdown>
      ) : null;
    });
  };

  const renderAttachmentPreview = () => {
    if (!chatAttachments || chatAttachments.length === 0) {
      return null;
    }

    return (
      <div className="relative z-10 mb-4 space-y-2">
        {chatAttachments.map((attachment, index) => {
          const isRejected = attachment.oversized || attachment.budgetRejected;
          return (
          <div key={index} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${isRejected ? 'border-red-500/40 bg-red-900/20' : 'border-white/10 bg-white/5'}`}>
            {attachment.previewUrl ? (
              <img
                src={attachment.previewUrl}
                alt={attachment.name}
                className="h-12 w-12 rounded-xl object-cover border border-white/10"
              />
            ) : (
              <div className={`flex h-12 w-12 items-center justify-center rounded-xl border text-xs font-semibold ${isRejected ? 'border-red-500/30 bg-red-900/30 text-red-300' : 'border-white/10 bg-white/10 text-gray-200'}`}>
                FILE
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className={`truncate text-sm font-medium ${isRejected ? 'text-red-300' : 'text-white'}`}>{attachment.name}</p>
              <p className={`text-xs ${isRejected ? 'text-red-400' : 'text-gray-400'}`}>
                {attachment.oversized && <span className="font-semibold">Exceeds 10 MB limit · </span>}
                {attachment.budgetRejected && <span className="font-semibold">Exceeds token limit · </span>}
                {attachment.mimeType} · {formatAttachmentSize(attachment.size)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => removeChatAttachment(index)}
              className={`rounded-full p-2 transition-colors ${isRejected ? 'text-red-400 hover:bg-red-900/30 hover:text-red-300' : 'text-gray-400 hover:bg-white/10 hover:text-white'}`}
              aria-label="Remove attachment"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          );
        })}
      </div>
    );
  };

  // Enviar mensaje al assistant con STREAMING
  const handleSendMessage = async () => {
    if (!chattingAssistant) return;

    // Block sending if any oversized attachment is present
    if (chatAttachments.some(a => a.oversized)) return;

    // Block sending if content budget has rejected blocks
    if (contentBudget?.rejected_blocks?.length > 0) return;

    const userContentBlocks = buildUserContentBlocks(chatInput, chatAttachments);

    if (userContentBlocks.length === 0) return;
    
    // Cancelar stream anterior si existe
    if (chatStreamControllerRef.current) {
      chatStreamControllerRef.current.abort();
    }
    
    // Detectar si es Zeno Bot
    const isZeno = chattingAssistant.id === ZENO_ASSISTANT_ID;
    
    // Verificar si el asistente permite archivos e imágenes (Zeno siempre permite)
    const allowsFilesAndImages = isZeno || chattingAssistant?.config?.acceptsFilesAndImages === true || chattingAssistant?.config?.accepts_files_and_images === true;
    
    setChatSending(true);
    setChatIsStreaming(true);
    const messageText = chatInput.trim();
    const attachmentsToSend = [...chatAttachments];
    
    // IDs para los mensajes temporales
    const userMessageId = `temp-user-${Date.now()}`;
    const assistantMessageId = `temp-assistant-${Date.now()}`;
    
    // Agregar mensaje del usuario (placeholder del assistant se agrega cuando empiece el streaming)
    setChatMessages((msgs) => [
      ...msgs, 
      { id: userMessageId, role: "user", content: messageText, contentBlocks: userContentBlocks }
    ]);
    
    setChatInput("");
    setChatAttachments([]);
    // NO limpiar contentBudget aquí - se limpia en onDone si el envío es exitoso
    if (chatFileInputRef.current) {
      chatFileInputRef.current.value = '';
    }
    
    try {
      // Si no hay chatHistoryId, crear uno primero
      let currentChatHistoryId = chatHistoryId;
      let isNewChatHistory = false;
      
      if (!currentChatHistoryId) {
        isNewChatHistory = true;
        const chatResult = await ChatService.createChatHistory({
          accountId,
          userId: user?.user_id,
          assistantId: chattingAssistant.id,
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          initialMessage: messageText || (attachmentsToSend.length > 0 ? attachmentsToSend[0].name : 'Attachment')
        });
        
        if (chatResult.success) {
          currentChatHistoryId = chatResult.data.id;
          setChatHistoryId(currentChatHistoryId);
        } else {
          error("Chat Error", chatResult.error?.message || "Error creating chat history");
          setChatMessages((msgs) => msgs.filter(m => m.id !== userMessageId && m.id !== assistantMessageId));
          setChatSending(false);
          setChatIsStreaming(false);
          return;
        }
      }
      
      let accumulatedContent = '';
      let hasAddedPlaceholder = false;
      // Structured display blocks (e.g. generated images) surfaced by the
      // backend in the final SSE metadata event. Rendered on done so the image
      // shows in the live turn without waiting for a reload.
      let displayBlocks = [];
      
      // Función para agregar el placeholder del assistant cuando comience el streaming
      const addAssistantPlaceholder = () => {
        if (!hasAddedPlaceholder) {
          hasAddedPlaceholder = true;
          setChatMessages((msgs) => [
            ...msgs,
            { id: assistantMessageId, role: "assistant", content: "", isStreaming: true }
          ]);
        }
      };
      
      // Callbacks comunes para streaming
      const streamCallbacks = {
        onContent: (chunk) => {
          // Agregar placeholder en el primer chunk
          addAssistantPlaceholder();
          
          accumulatedContent += chunk;
          // Actualizar el mensaje del assistant en tiempo real
          setChatMessages((msgs) => {
            const updated = [...msgs];
            const idx = updated.findIndex(m => m.id === assistantMessageId);
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], content: accumulatedContent };
            }
            return updated;
          });
        },
        onMetadata: (metadata) => {
          console.log('Chat metadata:', metadata);
          // Actualizar chatHistoryId si viene en metadata (para Zeno)
          if (metadata?.chatHistoryId && !chatHistoryId) {
            setChatHistoryId(metadata.chatHistoryId);
          }
          // Capturar contentBudget si viene en metadata
          if (metadata?.contentBudget || metadata?.content_budget) {
            const budget = metadata.contentBudget || metadata.content_budget;
            setContentBudget(budget);
          }
          // Capturar bloques de display (imágenes generadas, etc.)
          const blocks = metadata?.display_blocks || metadata?.displayBlocks;
          console.log('[onMetadata] display_blocks:', blocks);
          if (Array.isArray(blocks) && blocks.length > 0) {
            displayBlocks = blocks;
            console.log('[onMetadata] displayBlocks set:', displayBlocks);
          }
        },
        onDone: async () => {
          // PRIMERO: Ocultar cursor inmediatamente
          setChatIsStreaming(false);
          setChatSending(false);
          chatStreamControllerRef.current = null;
          
          // Limpiar contentBudget solo cuando el mensaje se envió exitosamente
          setContentBudget(null);
          
          // LUEGO: Finalizar el mensaje del assistant
          setChatMessages((msgs) => {
            const updated = [...msgs];
            const idx = updated.findIndex(m => m.id === assistantMessageId);
            if (idx !== -1) {
              const finalized = {
                ...updated[idx],
                content: accumulatedContent,
                isStreaming: false
              };
              // If the backend surfaced display blocks (e.g. a generated image),
              // render text + those blocks structurally so the image shows live.
              console.log('[onDone] displayBlocks at finalize:', displayBlocks);
              if (displayBlocks.length > 0) {
                finalized.contentBlocks = [
                  { type: 'text', data: { text: accumulatedContent }, order: 0 },
                  ...displayBlocks.map((b, i) => ({ ...b, order: i + 1 })),
                ];
              }
              updated[idx] = finalized;
            }
            return updated;
          });

          // Actualizar historial si es nuevo chat
          if (isNewChatHistory) {
            setTimeout(async () => {
              await fetchAllChatHistories();
            }, 1500);
          }
        },
        onError: (err) => {
          console.error('Streaming error:', err);
          
          // Check for content budget exceeded error
          if (err.status === 400 && err.data?.detail?.error === 'content_budget_exceeded') {
            const budgetData = err.data.detail.content_budget;
            if (budgetData) {
              budgetFromErrorRef.current = true;
              setContentBudget(budgetData);
              // Restore attachments, marking rejected ones so user knows which to remove
              const rejectedFilenames = new Set(
                (budgetData.rejected_blocks || []).map(b => b.filename).filter(Boolean)
              );
              const restored = attachmentsToSend.map(att => ({
                ...att,
                budgetRejected: rejectedFilenames.has(att.name),
              }));
              setChatAttachments(restored);
              setChatInput(messageText);
              // Remove the user message since it wasn't actually sent
              setChatMessages((msgs) => msgs.filter(m => m.id !== userMessageId));
              setChatSending(false);
              setChatIsStreaming(false);
              chatStreamControllerRef.current = null;
              return; // Don't show error as chat message - the red UI will show
            }
          }
          
          // Determinar el mensaje de error
          let errorMessage = err.message || "Error sending message";
          
          // Detectar error de cuota
          if (err.status === 429 || errorMessage.includes('quota exceeded')) {
            errorMessage = "⚠️ Quota exceeded. Please try again later or upgrade your plan.";
          } else if (err.status === 402) {
            errorMessage = "⚠️ Insufficient coins. Please add more coins to continue.";
          }
          
          // Mostrar error como mensaje del assistant en lugar de toast
          if (hasAddedPlaceholder) {
            // Si ya hay placeholder, actualizar con el error
            setChatMessages((msgs) => {
              const updated = [...msgs];
              const idx = updated.findIndex(m => m.id === assistantMessageId);
              if (idx !== -1) {
                updated[idx] = { 
                  ...updated[idx], 
                  content: errorMessage,
                  isStreaming: false,
                  isError: true
                };
              }
              return updated;
            });
          } else {
            // Si no hay placeholder, agregar mensaje de error
            setChatMessages((msgs) => [
              ...msgs,
              { 
                id: assistantMessageId, 
                role: "assistant", 
                content: errorMessage,
                isStreaming: false,
                isError: true
              }
            ]);
          }
          
          // También mostrar toast para errores de cuota
          if (err.status === 429 || err.status === 402) {
            error("Quota Exceeded", errorMessage);
          }
          
          setChatSending(false);
          setChatIsStreaming(false);
          chatStreamControllerRef.current = null;
        },
      };
      
      // Usar el servicio apropiado según si es Zeno o un assistant regular
      let controller;
      if (isZeno) {
        console.log('🔵 [handleSendMessage] Using ZenoService.chatWithZenoStream');
        // Usar ZenoService para Zeno Bot
        const result = ZenoService.chatWithZenoStream(
          {
            accountId,
            userId: user?.user_id,
            message: messageText,
            contentBlocks: userContentBlocks,
            chatHistoryId: currentChatHistoryId,
            context: null,
            workflowContext: null,
            useMemory: true,
          },
          streamCallbacks
        );
        controller = result.controller;
      } else {
        console.log('🟢 [handleSendMessage] Using ChatService.sendMessageStream');
        // Usar ChatService para assistants regulares
        const result = ChatService.sendMessageStream(
          {
            assistantId: chattingAssistant.id,
            chatHistoryId: currentChatHistoryId,
            message: messageText,
            contentBlocks: userContentBlocks,
            userId: user?.user_id,
            accountId,
          },
          streamCallbacks
        );
        controller = result.controller;
      }
      
      chatStreamControllerRef.current = controller;
      
    } catch (err) {
      error("Chat Error", "Unexpected error");
      console.error('Error sending message:', err);
      setChatMessages((msgs) => msgs.filter(m => m.id !== userMessageId && m.id !== assistantMessageId));
      setChatSending(false);
      setChatIsStreaming(false);
    }
  };

  // Cancelar streaming
  const handleCancelStream = () => {
    if (chatStreamControllerRef.current) {
      chatStreamControllerRef.current.abort();
      chatStreamControllerRef.current = null;
      setChatSending(false);
      setChatIsStreaming(false);
    }
  };

  // Cerrar chat
  const handleCloseChat = () => {
    handleCancelStream(); // Cancelar streaming si está activo
    setChattingAssistant(null);
    setChatMessages([]);
    setChatInput("");
    setChatAttachments([]);
    setContentBudget(null);
    setChatSending(false);
    setChatIsStreaming(false);
    setShowInputAnimation(false);
  };

  // Cargar chat histories al abrir el chat con assistant
  useEffect(() => {
    if (chattingAssistant && accountId) {
      // Cargar histories específicos de este assistant
      AssistantService.listChatHistoriesByAssistant({
        accountId,
        assistantId: chattingAssistant.id,
        limit: 15,
        offset: 0,
        sort_by: 'updated_at',
        descending: true
      }).then(result => {
        if (result.success && Array.isArray(result.data)) {
          setChatHistories(result.data);
          console.log(`📋 Loaded ${result.data.length} chat histories for ${chattingAssistant.name}`);
        } else {
          setChatHistories([]);
          console.log('No chat histories found for this assistant');
        }
      }).catch(err => {
        console.error('Error loading initial chat histories for assistant:', err);
        setChatHistories([]);
      });
    }
  }, [chattingAssistant, accountId]);

  if (creatingAvatarFlow) {
    return (
      <>
        <AvatarsView
          projectId={projectId}
          launchCreateMode={true}
          onBackToAssistantCreate={() => setCreatingAvatarFlow(false)}
          onTemplateCreated={handleReturnFromAvatarFlow}
        />
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      </>
    );
  }

  // ------------------ PANTALLA “NEW ASSISTANT” ------------------
  if (creating) {
    return (
      <div className="h-full min-h-[calc(100vh-10rem)] overflow-hidden">
      {createDependenciesLoading && templates.length === 0 ? (
        <div className="flex h-full flex-col gap-4 p-4 md:p-5">
          <div className="h-8 w-56 bg-white/10 rounded-lg skeleton" />
          <div className="h-24 bg-white/10 rounded-2xl skeleton" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-40 bg-white/10 rounded-2xl skeleton" />
            <div className="h-40 bg-white/10 rounded-2xl skeleton" />
          </div>
          <div className="h-56 bg-white/10 rounded-2xl skeleton" />
        </div>
      ) : (
      <AssistantCreateSteps
        submitting={submitting}
        step={createStep}
        onStepChange={setCreateStep}
        onCreateAvatar={handleCreateAvatarFromWizard}
        onBack={() => {
          resetForm();
          setCreating(false);
        }}
        onCreate={handleCreate}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        avatarBase64={avatarBase64}
        setAvatarBase64={setAvatarBase64}
        avatarUrl={avatarUrl}
        setAvatarUrl={setAvatarUrl}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        llmModels={llmModels}
        templates={templates}
        projects={projects}
        selectedProjectId={selectedProjectId}
        setSelectedProjectId={setSelectedProjectId}
        requireProjectSelection={!projectId}
        accountId={activeAccountId}
        executionMode={executionMode}
        setExecutionMode={setExecutionMode}
        selectedSkills={selectedSkills}
        setSelectedSkills={setSelectedSkills}
        skillSettings={skillSettings}
        setSkillSettings={setSkillSettings}
        skillIntegrations={skillIntegrations}
        setSkillIntegrations={setSkillIntegrations}
        memoryEnabled={memoryEnabled}
        setMemoryEnabled={setMemoryEnabled}
        acceptsFilesAndImages={acceptsFilesAndImages}
        setAcceptsFilesAndImages={setAcceptsFilesAndImages}
        isDraft={isDraft}
        setIsDraft={setIsDraft}
        maxTokens={maxTokens}
        setMaxTokens={setMaxTokens}
        temperature={temperature}
        setTemperature={setTemperature}
        availableSkills={availableSkills}
        missingSkillIntegrations={missingSkillIntegrations}
        checkingIntegrations={checkingIntegrations}
        onGoToIntegrations={handleGoToIntegrations}
        validationErrors={validationErrors}
        clearValidationError={clearValidationError}
      />
      )}
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      </div>
    )
  }

  // ------------------ PANTALLA "EDIT ASSISTANT" ------------------
  if (editing && editingAssistant) {
    // Sincronizar valores iniciales de modelo y template con el assistant seleccionado
    const initialModelId = editingAssistant?.config?.llm_id || selectedModel;
    const initialTemplateId = editingAssistant?.config?.assistant_template?.id || selectedTemplate;

    return (
      <div className="h-full min-h-[calc(100vh-10rem)] overflow-y-auto">
        <AssistantConfigureView
          assistant={editingAssistant}
          onAssistantChange={setEditingAssistant}
          onBack={handleBackFromEditing}
          onSave={handleCreate}
          onUpdate={handleUpdate}
          isUpdating={submitting}
          llmModels={llmModels}
          templates={templates}
          initialModelId={initialModelId}
          initialTemplateId={initialTemplateId}
          sections={sections}
          setSections={setSections}
        />
      </div>
    )
  }

  // ------------------ VISTA PRINCIPAL GRID/LIST ------------------
  return (
    <div className="flex min-h-0 opacity-0 animate-blur-reveal [animation-fill-mode:forwards]">


      <div className={`flex-1 ${showSidebar ? 'pl-4 md:pl-8 lg:pl-12 xl:pl-16' : ''} mt-8`}>
        {/* Header: mostrar botones solo si NO está en chat */}
        {!chattingAssistant ? (
          <>
            {/* Toolbar: title · search · status filter · view mode · create */}
            <div
              className="mb-4 flex flex-col gap-4 opacity-0 animate-reveal-down [animation-fill-mode:forwards] xl:flex-row xl:items-center xl:justify-between"
              style={{ animationDelay: '0.05s' }}
            >
              {/* Dentro de una sección con pestañas, el nombre lo pone la
                  pestaña: repetirlo aquí era decir "Agents" dos veces. */}
              {!hideTitle && (
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Agents</h1>
                  <p className="text-xs text-gray-400 sm:text-sm">
                    Build and manage the AI agents that talk to your customers.
                  </p>
                </div>
              )}

              <div className="relative flex-1 xl:max-w-md">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search agents by name, description, or model..."
                  className="w-full rounded-xl border border-white/10 bg-black/40 py-2.5 pl-10 pr-10 text-sm text-white placeholder-gray-500 backdrop-blur-sm transition-colors focus:border-violet-500/50 focus:outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-white"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!projectId && projects.length > 0 && (
                  <StyledSelect
                    value={urlProjectId}
                    onChange={handleProjectFilter}
                    options={[
                      { value: "", label: "All projects" },
                      ...projects.map((p) => ({ value: p.project_id, label: p.name || "Untitled project" })),
                    ]}
                    placeholder="All projects"
                    className="w-full min-w-0 sm:w-48"
                  />
                )}

                <div className="flex shrink-0 rounded-lg border border-white/10 bg-black/50 p-1">
                  <button
                    onClick={() => setViewMode("grid")}
                    aria-label="Grid view"
                    className={`rounded-md p-2 transition-colors ${
                      viewMode === "grid" ? "bg-white/15 text-white" : "text-gray-400 hover:text-white"
                    }`}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    aria-label="List view"
                    className={`rounded-md p-2 transition-colors ${
                      viewMode === "list" ? "bg-white/15 text-white" : "text-gray-400 hover:text-white"
                    }`}
                  >
                    <LayoutList className="h-4 w-4" />
                  </button>
                </div>

                {/* Con etiqueta también en móvil. Era un "+" a secas, y un "+"
                    suelto no dice qué crea: en una pantalla que ya tiene
                    proyectos, agentes y avatares, esa duda cuesta más que los
                    90px que ocupa la palabra. El que cede ancho es el selector
                    de proyecto, que se trunca sin perder sentido. */}
                <Button
                  onClick={handleOpenCreate}
                  disabled={assistantsLoading}
                  aria-label="Create agent"
                  title="Create agent"
                  className="h-10 shrink-0 whitespace-nowrap px-3 text-sm bg-primary text-white transition-colors hover:bg-violet-700 disabled:opacity-50 sm:px-5"
                >
                  <Plus className="mr-1.5 h-4 w-4 sm:mr-2" />
                  Create agent
                </Button>
              </div>
            </div>

            {/* Stats strip */}
            <div
              className="mb-6 rounded-2xl border border-white/10 bg-white/[0.03] p-3 opacity-0 animate-reveal-down [animation-fill-mode:forwards] sm:p-4"
              style={{ animationDelay: '0.08s' }}
            >
              {/* Tres en una fila también en móvil: son tres números cortos que
                  se leen juntos, y a dos columnas el tercero se quedaba solo
                  en una segunda fila a medio ancho. */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <StatTile Icon={Headset} tone="pink" label="Total Agents" value={agentStats.total} sub="Across all projects" />
                <StatTile Icon={Wrench} label="With Skills" value={agentStats.withSkills} sub="Using tools" approx={assistantsHasMore} />
                <StatTile
                  Icon={Cpu}
                  label="Top Model"
                  value={agentStats.topModel || "—"}
                  sub={agentStats.topModel ? "Most used" : "No agents yet"}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between mb-8 opacity-0 animate-reveal-down [animation-fill-mode:forwards]" style={{ animationDelay: '0.1s' }}>
            <h2 className="text-3xl font-bold text-white">{chattingAssistant.name}</h2>
            <Button
              onClick={handleCloseChat}
              className="bg-primary hover:bg-violet-700 text-white px-5 py-2 rounded-xl font-light text-lg flex items-center transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25"
            >
              <X className="w-4 h-4 mr-2" />
              Close Chat
            </Button>
          </div>
        )}

        {/* Chat History Sidebar - estilo Zeno glassmorphism */}
        {chattingAssistant && showSidebar && (
          <>
            {/* Overlay para cerrar sidebar al hacer click fuera */}
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300"
              onClick={closeSidebar}
            />
            
            {/* Sidebar panel */}
            <div className="fixed top-0 right-0 h-full w-96 bg-gray-900/95 backdrop-blur-xl border-l border-white/10 z-50 shadow-2xl transform transition-transform duration-300 ease-in-out">
              {/* Header del sidebar */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Chat History</h3>
                  <button
                    onClick={closeSidebar}
                    className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-400">
                  {chatHistories.length} conversation{chatHistories.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Lista de historiales con scroll */}
              <div 
                className="overflow-y-auto p-4 space-y-3" 
                id="chat-history-scroll-chat"
                style={{ height: 'calc(100vh - 120px)' }}
              >
                {loadingHistories ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 text-purple-400 animate-spin" />
                  </div>
                ) : chatHistories.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    No chat history yet
                  </div>
                ) : (
                  chatHistories.map((history) => (
                    <div
                      key={history.id}
                      onClick={async () => {
                        await handleSelectHistory(history);
                        closeSidebar();
                      }}
                      className={`
                        p-4 rounded-xl cursor-pointer transition-all duration-300
                        ${chatHistoryId === history.id 
                          ? 'bg-purple-600/20 border border-purple-500/50' 
                          : 'bg-gray-800/50 border border-gray-700/30 hover:border-purple-500/30 hover:bg-gray-800/70'
                        }
                        backdrop-blur-sm
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <MessageSquare className={`w-5 h-5 mt-1 flex-shrink-0 ${chatHistoryId === history.id ? 'text-purple-400' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {history.title || 'Untitled conversation'}
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            {new Date(history.created_at).toLocaleDateString()} • {history.message_count || 0} messages
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Chat con assistant: vista centrada estilo Zeno */}
        {chattingAssistant ? (
          (() => {
            // Calcular si el asistente permite archivos e imágenes (Zeno siempre permite)
            const isZeno = chattingAssistant.id === ZENO_ASSISTANT_ID;
            const allowsFilesAndImages = isZeno || chattingAssistant?.config?.acceptsFilesAndImages === true || chattingAssistant?.config?.accepts_files_and_images === true;
            
            return (
          <div 
            className={`w-full max-w-4xl lg:max-w-5xl flex flex-col mx-auto px-4 ${chatMessages.length === 0 ? 'h-auto min-h-[60vh]' : 'h-[calc(70vh-4rem)]'}`}
          >
            <input
              ref={chatFileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleAttachmentSelected}
            />
            {chatMessages.length === 0 ? (
              /* Estado vacío - input más arriba */
              <div className="flex-1 flex flex-col items-center justify-center gap-6 -mt-32">
                <div className="text-center">
                  <p className="text-gray-400 text-lg">Start the conversation!</p>
                </div>
                {/* Input centrado */}
                <div ref={chatInputAreaRef} className="relative w-full mx-auto">
                  <div className="relative overflow-hidden rounded-[32px] border border-white/15 backdrop-blur-[20px] bg-[linear-gradient(180deg,rgba(46,51,90,0)_0%,rgba(28,27,51,0.1)_100%)] shadow-[0_30px_60px_0px_rgba(0,0,0,0.1),0_15px_30px_0px_rgba(0,0,0,0.05),0_5px_10px_0px_rgba(0,0,0,0.05),inset_0_0_100px_0px_rgba(204,215,255,0.15)] p-4 sm:p-6">
                    {/* Dot pattern background */}
                    <div className="pointer-events-none absolute inset-0 rounded-[inherit]" style={{backgroundImage: 'repeating-radial-gradient(circle, rgba(255, 255, 255, 0.05) 0px, rgba(255, 255, 255, 0.05) 0.5px, transparent 0.5px, transparent 12px)', backgroundSize: '15px 15px'}} />
                    {/* Radial gradient overlay */}
                    <div className="pointer-events-none absolute inset-0 rounded-[inherit]" style={{background: 'radial-gradient(circle, transparent 60%, rgba(255, 255, 255, 0.08) 100%)'}} />
                    {renderAttachmentPreview()}
                    
                    <div className="relative z-10 flex items-center gap-4">
                      {allowsFilesAndImages && (
                      <button
                        className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 w-10 text-gray-400 hover:text-white hover:bg-white/10 rounded-full flex-shrink-0"
                        type="button"
                        aria-label="Attach file"
                        onClick={handleAttachmentClick}
                        title="Attach a file"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-paperclip w-6 h-6">
                          <path d="M13.234 20.252 21 12.3"></path>
                          <path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486"></path>
                        </svg>
                      </button>
                      )}
                      
                      <textarea
                        className="flex w-full rounded-md border-input px-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex-1 bg-transparent border-0 text-white text-lg placeholder:text-gray-500 resize-none min-h-[60px] max-h-[200px] focus:ring-0 focus:outline-none py-3"
                        placeholder="Type your message..."
                        rows={1}
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => { 
                          if (e.key === "Enter" && !e.shiftKey && !chatSending && !(contentBudget?.rejected_blocks?.length > 0)) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={chatSending}
                      />
                      
                      <button
                        type="button"
                        onClick={openSidebar}
                        className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2 flex-shrink-0 transition-colors"
                      >
                        <MessageSquare className="w-6 h-6" />
                      </button>
                      
                      <button
                        onClick={handleSendMessage}
                        disabled={chatSending || (!chatInput.trim() && chatAttachments.length === 0) || (contentBudget?.rejected_blocks && contentBudget.rejected_blocks.length > 0) || chatAttachments.some(a => a.oversized)}
                        className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-12 w-12 bg-primary hover:bg-purple-500 text-white rounded-full flex-shrink-0"
                        type="button"
                        aria-label="Send message"
                      >
                        {chatSending ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-send w-5 h-5"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/><path d="m21.854 2.147-10.94 10.939"/></svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Estado con mensajes - layout con scroll */
              <>
                {/* Messages area con scroll */}
                <div className="flex-1 overflow-y-auto mb-4" ref={chatScrollContainerRef}>
                  <div className="space-y-6 px-0 py-6" id="chat-message-scroll-area">
                    {/* Content Budget Error */}
                    {contentBudget && contentBudget.rejected_blocks && contentBudget.rejected_blocks.length > 0 && (
                      <div className="mx-8 mb-6 rounded-2xl border border-red-500/40 bg-red-900/30 backdrop-blur-sm p-5">
                        <div className="flex items-start gap-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-red-200 font-semibold text-sm">
                                Cannot send — {contentBudget.rejected_blocks.length} {contentBudget.rejected_blocks.length === 1 ? 'file' : 'files'} exceed token limit
                              </h4>
                              <button 
                                onClick={() => setContentBudget(null)}
                                className="text-red-400/60 hover:text-red-300 transition-colors"
                                aria-label="Dismiss"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-red-300/70 text-xs mb-3">
                              Your attached files use too many tokens. Remove some files to free up space — larger files have more impact.
                            </p>
                            {contentBudget.input_budget && (contentBudget.input_used || contentBudget.used_tokens) && (
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-xs text-red-300/60 mb-1">
                                  <span>Token usage</span>
                                  <span>{(contentBudget.input_used || contentBudget.used_tokens).toLocaleString()} / {contentBudget.input_budget.toLocaleString()}</span>
                                </div>
                                <div className="h-1.5 bg-red-900/50 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-red-500/70 rounded-full"
                                    style={{ width: `${Math.min(100, ((contentBudget.input_used || contentBudget.used_tokens) / contentBudget.input_budget) * 100)}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            <p className="text-red-300/50 text-xs mb-2">Files that don't fit:</p>
                            <div className="space-y-1.5">
                              {contentBudget.rejected_blocks.map((block, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-red-200/80 text-sm">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-400/70 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate">{block.filename || 'Unnamed file'}</span>
                                  {block.estimated_tokens > 0 && (
                                    <span className="text-red-400/50 text-xs ml-auto whitespace-nowrap">
                                      ~{block.estimated_tokens.toLocaleString()} tokens
                                    </span>
                                  )}
                                  {block.reason && block.reason !== 'input_budget_exceeded' && (
                                    <span className="text-red-400/50 text-xs ml-auto whitespace-nowrap">
                                      ({block.reason.replace(/_/g, ' ')})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-6">
                      {chatMessages.map((msg, idx) => (
                        <div key={msg.id || idx} className={`flex w-full ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`relative overflow-hidden max-w-[85%] sm:max-w-xl w-fit text-sm ${
                              msg.role === "user" 
                                ? "backdrop-blur-[20px] bg-gradient-to-br from-purple-500/20 via-purple-600/15 to-blue-500/20 text-gray-100 rounded-2xl rounded-br-md px-3 sm:px-4 py-2 sm:py-3 mr-2 sm:mr-6 mt-2 border border-purple-400/30 shadow-lg shadow-purple-900/20 animate-bubble-in-right" 
                                : msg.isError 
                                  ? "backdrop-blur-[20px] bg-gradient-to-br from-red-500/20 via-red-600/15 to-red-500/10 border border-red-400/30 text-red-200 rounded-2xl rounded-bl-md px-3 sm:px-4 py-2 sm:py-3 ml-2 sm:ml-6 mt-2 shadow-lg shadow-red-900/20 animate-bubble-in"
                                  : "backdrop-blur-[20px] bg-gradient-to-br from-purple-500/15 via-blue-500/10 to-purple-600/15 text-gray-100 rounded-2xl rounded-bl-md px-3 sm:px-4 py-2 sm:py-3 border border-purple-400/25 ml-2 sm:ml-6 mt-2 shadow-lg shadow-purple-900/10 animate-bubble-in"
                            }`}
                          >
                            {/* Dot pattern sutil */}
                            <div className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-30" style={{backgroundImage: 'repeating-radial-gradient(circle, rgba(255, 255, 255, 0.05) 0px, rgba(255, 255, 255, 0.05) 0.5px, transparent 0.5px, transparent 12px)', backgroundSize: '15px 15px'}} />
                            
                            <div className="relative z-10">
                              {msg.role !== "user" && !msg.isError && (
                                <div className="flex items-center gap-2 mb-2">
                                  <img 
                                    src={chattingAssistant.avatar || assistantsBot} 
                                    alt={chattingAssistant.name}
                                    className="w-5 h-5 rounded-full object-cover ring-1 ring-purple-400/40"
                                  />
                                  <span className="text-xs font-medium text-purple-200">{chattingAssistant.name || 'Zeno'}</span>
                                </div>
                              )}
                              {Array.isArray(msg.contentBlocks)
                              ? renderMessageContentBlocks(msg)
                              : (
                                  <>
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={assistantMarkdownComponents}
                                    >
                                      {normalizeAssistantMarkdown(msg.content || (msg.isStreaming ? '' : '[No content]'))}
                                    </ReactMarkdown>
                                    {msg.isStreaming && chatIsStreaming && (
                                      <span className="inline-block w-2 h-4 ml-1 bg-purple-400 animate-pulse" />
                                    )}
                                  </>
                                )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Indicador de "thinking" mientras se espera el streaming */}
                      {chatSending && !chatMessages.some(m => m.isStreaming) && (
                        <div className="flex w-full justify-start">
                          <div className="relative overflow-hidden max-w-[85%] sm:max-w-xl w-fit backdrop-blur-[20px] bg-gradient-to-br from-purple-500/15 via-blue-500/10 to-purple-600/15 text-gray-100 rounded-2xl rounded-bl-md px-3 sm:px-4 py-2 sm:py-3 border border-purple-400/25 ml-2 sm:ml-6 mt-2 text-sm shadow-lg shadow-purple-900/10 animate-bubble-in">
                            {/* Dot pattern sutil */}
                            <div className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-30" style={{backgroundImage: 'repeating-radial-gradient(circle, rgba(255, 255, 255, 0.05) 0px, rgba(255, 255, 255, 0.05) 0.5px, transparent 0.5px, transparent 12px)', backgroundSize: '15px 15px'}} />
                            
                            <div className="relative z-10">
                              <div className="flex items-center gap-2 mb-2">
                                <img 
                                  src={chattingAssistant.avatar || assistantsBot} 
                                  alt={chattingAssistant.name}
                                  className="w-5 h-5 rounded-full object-cover ring-1 ring-purple-400/40"
                                />
                                <span className="text-xs font-medium text-purple-200">{chattingAssistant.name || 'Zeno'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <ZenitThinking size={16} />
                                <span className="text-gray-300">Thinking...</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Elemento para auto-scroll */}
                      <div ref={chatMessagesEndRef} />
                    </div>
                  </div>
                </div>
                {/* Input area - siempre visible */}
                <div ref={chatInputAreaRef} className={`relative w-full mx-auto flex-shrink-0 ${showInputAnimation ? 'animate-slide-down' : ''}`}>
                  <div className="relative overflow-hidden rounded-[32px] border border-white/15 backdrop-blur-[20px] bg-[linear-gradient(180deg,rgba(46,51,90,0)_0%,rgba(28,27,51,0.1)_100%)] shadow-[0_30px_60px_0px_rgba(0,0,0,0.1),0_15px_30px_0px_rgba(0,0,0,0.05),0_5px_10px_0px_rgba(0,0,0,0.05),inset_0_0_100px_0px_rgba(204,215,255,0.15)] p-4 sm:p-6">
                    {/* Dot pattern background */}
                    <div className="pointer-events-none absolute inset-0 rounded-[inherit]" style={{backgroundImage: 'repeating-radial-gradient(circle, rgba(255, 255, 255, 0.05) 0px, rgba(255, 255, 255, 0.05) 0.5px, transparent 0.5px, transparent 12px)', backgroundSize: '15px 15px'}} />
                    {/* Radial gradient overlay */}
                    <div className="pointer-events-none absolute inset-0 rounded-[inherit]" style={{background: 'radial-gradient(circle, transparent 60%, rgba(255, 255, 255, 0.08) 100%)'}} />
                    {renderAttachmentPreview()}
                    
                    <div className="relative z-10 flex items-center gap-4">
                      {allowsFilesAndImages && (
                      <button
                        className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 w-10 text-gray-400 hover:text-white hover:bg-white/10 rounded-full flex-shrink-0"
                        type="button"
                        aria-label="Attach file"
                        onClick={handleAttachmentClick}
                        title="Attach a file"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-paperclip w-6 h-6">
                          <path d="M13.234 20.252 21 12.3"></path>
                          <path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486"></path>
                        </svg>
                      </button>
                      )}
                      
                      <textarea
                        className="flex w-full rounded-md border-input px-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex-1 bg-transparent border-0 text-white text-lg placeholder:text-gray-500 resize-none min-h-[60px] max-h-[200px] focus:ring-0 focus:outline-none py-3"
                        placeholder="Type your message..."
                        rows={1}
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => { 
                          if (e.key === "Enter" && !e.shiftKey && !chatSending && !(contentBudget?.rejected_blocks?.length > 0)) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={chatSending}
                      />
                      
                      <button
                        onClick={async () => {
                          if (!chattingAssistant) return;
                          setChatMessages([]);
                          setChatInput("");
                          setChatAttachments([]);
                          setChatHistoryId(null);
                          if (!showSidebar) {
                            await openSidebar();
                          }
                        }}
                        type="button"
                        className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2 flex-shrink-0 transition-all duration-300 hover:scale-110"
                        aria-label="Chat history"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-square w-6 h-6">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                      </button>
                      
                      {chatIsStreaming ? (
                        <button
                          onClick={handleCancelStream}
                          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none bg-red-600 hover:bg-red-700 text-white rounded-full h-12 w-12 p-0 flex-shrink-0 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-500/30"
                          title="Stop generating"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      ) : (
                        <button
                          onClick={handleSendMessage}
                          disabled={chatSending || (!chatInput.trim() && chatAttachments.length === 0) || chatAttachments.some(a => a.oversized) || (contentBudget?.rejected_blocks && contentBudget.rejected_blocks.length > 0)}
                          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none bg-primary hover:bg-violet-700 text-white rounded-full h-12 w-12 p-0 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
                          type="submit"
                        >
                          {chatSending ? (
                            <Loader className="w-6 h-6 animate-spin" />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-send w-6 h-6">
                              <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path>
                              <path d="m21.854 2.147-10.94 10.939"></path>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
            );
          })()
        ) : (
          // Cards de assistants solo si NO hay chat abierto
          <React.Fragment>
            {/* Mostrar loading o estado vacío si no hay asistentes para el proyecto actual */}
            {assistantsLoading ? (
              /* Skeleton loading cards */
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(220px,220px))] justify-center">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div 
                    key={i} 
                    className="rounded-3xl overflow-hidden w-[220px] bg-white/5 border border-white/10"
                  >
                    <div className="pt-4 pb-3 flex items-center justify-center">
                      <div className="w-[140px] h-[140px] rounded-lg bg-white/10 skeleton" />
                    </div>
                    <div className="px-4 pb-4 space-y-3">
                      <div className="h-4 w-3/4 bg-white/10 rounded skeleton" />
                      <div className="h-3 w-full bg-white/10 rounded skeleton" />
                      <div className="h-3 w-2/3 bg-white/10 rounded skeleton" />
                      <div className="flex justify-between pt-2">
                        <div className="h-6 w-16 bg-white/10 rounded-full skeleton" />
                        <div className="flex gap-2">
                          <div className="h-8 w-8 bg-white/10 rounded-lg skeleton" />
                          <div className="h-8 w-8 bg-white/10 rounded-lg skeleton" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {filteredAssistants.length === 0 ? (
                  <div className="text-center py-20 opacity-0 animate-scale-reveal [animation-fill-mode:forwards]">
                    <div className="text-gray-400 text-lg mb-4">
                      {searchQuery || statusFilter !== "all"
                        ? "No agents match these filters"
                        : "No agents yet"}
                    </div>
                  </div>
                ) : (
                  <div
                    className={
                      viewMode === "grid"
                        ? "grid grid-cols-3 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                        : "space-y-2"
                    }
                  >
                    {filteredAssistants.map((assistant) => (
                      <AssistantCard
                        key={assistant.id}
                        assistant={assistant}
                        viewMode={viewMode}
                        onClick={() => openAgentWorkspace(assistant)}
                        onChatClick={handleOpenChat}
                        onDelete={handleDelete}
                        assistantsBot={assistantsBot}
                      />
                    ))}
                  </div>
                )}

                {/* Infinite scroll sentinel — kept outside the empty state so a
                    filter that matches nothing on this page keeps pulling pages. */}
                {assistantsHasMore && (
                  <div ref={agentsSentinelRef} className="flex items-center justify-center py-8">
                    {loadingMoreAssistants && (
                      <span className="flex items-center gap-2 text-sm text-gray-400">
                        <Loader className="h-4 w-4 animate-spin" />
                        Loading more agents…
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </React.Fragment>
        )}
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Agent"
        message="This action cannot be undone. Are you sure you want to delete this agent?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      </div>
    </div>
  );
}
// Updated: 2026-03-09 14:42:11

